import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridCellRenderer } from './GridCellRenderer';
import { useCommitAutoSave } from './useCommitAutoSave';
import {
  normalizeFilterValue, rowPassesColumnFilters, filterDisplayLabel, type ColumnFilters,
} from './gridFilterUtils';
import type { GridFilterValueOption } from './GridColumnFilter';
import { EditableGridHead } from './EditableGridHead';
import { GridScrollArrows } from './GridScrollArrows';
import {
  computeFrozenOffsets,
  computeFrozenPaneWidth,
  frozenEdgeSx,
  layeredBg,
  useHorizontalScrollState,
  useMeasuredFrozenOffsets,
  useViewportFitMaxHeight,
} from './gridFreeze';

export interface GridColumn {
  field: string;
  headerName: string;
  type: 'text' | 'number' | 'select' | 'date' | 'actions';
  required?: boolean;
  readonly?: boolean;
  /**
   * Per-CELL readonly (RBAC overhaul Phase C — skill-gated signoff cells).
   * Overrides `readonly` for individual rows: return true to lock the cell,
   * a string to lock it AND show that string as a tooltip explaining why,
   * or false/undefined to leave it editable.
   */
  getCellReadonly?: (row: Record<string, unknown>) => boolean | string | undefined;
  /**
   * Per-ROW select options (Phase C: signoff dropdowns scoped to the row's
   * test skill). Takes precedence over `options` when it returns a list.
   */
  getOptionsForRow?: (row: Record<string, unknown>) => string[] | undefined;
  /**
   * Rich per-row options: name + the person's skill level in the ROW's test
   * skill, rendered with a SkillLevelChip (same idiom as the scenario-planning
   * assignee picker). Takes precedence over getOptionsForRow/options when it
   * returns a non-empty list; the cell still stores the plain `value` string,
   * so save/gate/rollback behavior is unchanged.
   */
  getRichOptionsForRow?: (row: Record<string, unknown>) =>
    Array<{ value: string; level?: number | null }> | undefined;
  options?: readonly string[] | string[];
  /**
   * Key/label option pairs for a `select` column whose STORED value differs from
   * its DISPLAYED label (e.g. a GUID stored, a human name shown — REQ-06 round 2
   * "Default Machine" picker). Takes precedence over `options` when present; the
   * cell stores `value` and renders `label` both in the dropdown and read mode.
   */
  optionItems?: readonly { value: string; label: string }[];
  /** Show an "Add new…" entry at the bottom of the select dropdown. */
  allowCustomOption?: boolean;
  /** Called when user adds a custom option via the "Add new…" entry. */
  onCustomOptionAdded?: (newOption: string) => void;
  width?: number;
  flex?: number;
  /** Show browser-native spin buttons on number inputs (up/down arrows). */
  showSpinButtons?: boolean;
  /** Format number as currency ($1,234.00) when not editing. */
  formatCurrency?: boolean;
  /**
   * Currency code used when `formatCurrency` is true. Defaults to 'CAD'.
   * Stored values are in CAD; set this (with `fxRateToCad`) to display a
   * single-currency quote in its chosen currency (BUG-2026-0120 #6).
   */
  currency?: string;
  /**
   * FX rate (CAD per 1 unit of `currency`) used to convert the stored CAD value
   * for display when `currency` is not CAD. 1.0 / null → no conversion.
   */
  fxRateToCad?: number | null;
}

interface EditableGridProps {
  columns: GridColumn[];
  data: Record<string, unknown>[];
  onRowChange: (rowIndex: number, field: string, value: unknown) => void;
  onRowSave: (row: Record<string, unknown>) => void;
  onSelectionChange: (selectedKeys: string[]) => void;
  keyField: string;
  allowAdd: boolean;
  onAddRow: () => void;
  /** Unused by the grid itself (deletes ride onRowDelete) — kept optional for
   *  legacy callers that still pass them. */
  allowDelete?: boolean;
  onDelete?: (selectedKeys: string[]) => void;
  onAction?: (row: Record<string, unknown>) => void;
  onRowClick?: (row: Record<string, unknown>) => void;
  onRowDelete?: (row: Record<string, unknown>) => void;
  /** When set, the actions cell gains a history icon (audit trail, mig 110). */
  onRowHistory?: (row: Record<string, unknown>) => void;
  /** When true, all cells are read-only — no editing, no save triggers */
  readOnly?: boolean;
  /**
   * Excel-style per-column value filters (BUG-2026-0143). Opt-in: each column
   * header gets a funnel icon opening a distinct-value checklist. Off by
   * default so existing grids are unaffected.
   */
  enableColumnFilters?: boolean;
  /**
   * Freeze the leading N data columns (plus the checkbox gutter) so they stay
   * put while scrolling horizontally (BUG-2026-0159). Opt-in, default 0.
   */
  freezeColumns?: number;
  /**
   * Cap the grid body to the remaining viewport height and keep the header
   * row sticky while its rows scroll (BUG-2026-0159). Opt-in, default off.
   */
  stickyHeader?: boolean;
  /**
   * Floating ◀/▶ hover arrows for horizontal paging — same affordance as the
   * schedule rail's week arrows. Pairs best with stickyHeader. Default off.
   */
  showScrollArrows?: boolean;
  /**
   * Save the row as soon as a `select` or `date` cell commits a value, instead
   * of waiting for the user to click outside the row. Selects save ~250ms after
   * the pick; dates debounce ~800ms per change (the native input fires per
   * completed segment while typing) and hold while the year is obviously
   * mid-typing (e.g. 0002). Text/number cells keep the on-blur behavior
   * (saving per keystroke would spam the API). Opt-in so existing grids are
   * unaffected.
   */
  autoSaveCells?: boolean;
}

export const EditableGrid = ({
  columns,
  data,
  onRowChange,
  onRowSave,
  onSelectionChange,
  keyField,
  allowAdd,
  onAddRow,
  onAction,
  onRowClick,
  onRowDelete,
  onRowHistory,
  readOnly = false,
  enableColumnFilters = false,
  freezeColumns = 0,
  stickyHeader = false,
  showScrollArrows = false,
  autoSaveCells = false,
}: EditableGridProps) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const freezeCount = Math.max(0, Math.min(freezeColumns, columns.length));
  // Config-derived estimate for the first paint; the measured values take
  // over immediately after mount and track the table's REAL column widths
  // (BUG-2026-0159 rework — see useMeasuredFrozenOffsets).
  const estimatedOffsets = useMemo(() => computeFrozenOffsets(columns, freezeCount), [columns, freezeCount]);
  const estimatedPaneWidth = useMemo(() => computeFrozenPaneWidth(columns, freezeCount), [columns, freezeCount]);
  const measuredFrozen = useMeasuredFrozenOffsets(containerRef, columns, freezeCount, data.length);
  const frozenOffsets = measuredFrozen?.offsets ?? estimatedOffsets;
  const frozenPaneWidth = measuredFrozen?.paneWidth ?? estimatedPaneWidth;
  // The empty state below returns early without the TableContainer, so gate
  // both hooks on the table actually existing — their effects re-arm when it
  // (re)mounts (empty → populated) instead of holding a dead null ref.
  const hasTable = data.length > 0 || allowAdd;
  const maxBodyHeight = useViewportFitMaxHeight(stickyHeader && hasTable, containerRef);
  const { canScrollLeft, canScrollRight, pageBy } =
    useHorizontalScrollState(containerRef, showScrollArrows && hasTable);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  const handleSortClick = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const anyFilterActive = Object.keys(columnFilters).length > 0;

  // Filter → sort, keeping each row's ORIGINAL index in `data`. Every edit/save
  // callback reasons in original-index space (parents mutate their own unsorted
  // arrays by that index), so sorted/filtered views must never leak display
  // positions into rowIndex.
  const displayRows = useMemo(() => {
    let rows = data.map((row, index) => ({ row, index }));
    if (anyFilterActive) rows = rows.filter(({ row }) => rowPassesColumnFilters(row, columnFilters));
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const av = a.row[sortField] ?? '';
        const bv = b.row[sortField] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, columnFilters, anyFilterActive, sortField, sortDir]);

  // Distinct values offered per column, Excel-cascade style: a column's
  // checklist reflects only rows that pass the OTHER columns' filters.
  const filterOptionsByField = useMemo(() => {
    if (!enableColumnFilters) return {} as Record<string, GridFilterValueOption[]>;
    const map: Record<string, GridFilterValueOption[]> = {};
    for (const col of columns) {
      if (col.type === 'actions' || !col.headerName) continue;
      const counts = new Map<string, number>();
      for (const row of data) {
        if (!rowPassesColumnFilters(row, columnFilters, col.field)) continue;
        const v = normalizeFilterValue(row[col.field]);
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      map[col.field] = Array.from(counts.entries())
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .map(([value, count]) => ({ value, label: filterDisplayLabel(col, value), count }));
    }
    return map;
  }, [enableColumnFilters, columns, data, columnFilters]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const pendingSaveRef = useRef<number | null>(null);
  // Dirty rows kept as state (so visuals re-render) + ref-mirror (so the
  // synchronous focus/blur callbacks always read the latest set without going
  // stale through closure capture).
  const [dirtyRows, setDirtyRows] = useState<Set<number>>(new Set());
  const dirtyRowsRef = useRef(dirtyRows);
  dirtyRowsRef.current = dirtyRows;
  const markRowDirty = useCallback((rowIndex: number) => {
    setDirtyRows((prev) => {
      if (prev.has(rowIndex)) return prev;
      const next = new Set(prev);
      next.add(rowIndex);
      return next;
    });
  }, []);
  const clearRowDirty = useCallback((rowIndex: number) => {
    setDirtyRows((prev) => {
      if (!prev.has(rowIndex)) return prev;
      const next = new Set(prev);
      next.delete(rowIndex);
      return next;
    });
  }, []);
  const dataRef = useRef(data);
  dataRef.current = data; // always latest

  // Safety net for "what if someone forgets to click outside?" — warn the
  // browser if the user tries to close / reload the tab while any rows are
  // dirty. Does NOT trigger for in-app navigation (React Router) — that's
  // out of scope here; on-blur save catches that case.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRowsRef.current.size === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Best-effort flush on unmount (in-app navigation away from the grid).
  // Saves any rows still dirty so the user doesn't lose them.
  useEffect(() => {
    return () => {
      const dirty = Array.from(dirtyRowsRef.current);
      for (const rowIndex of dirty) {
        const row = dataRef.current[rowIndex];
        if (row) {
          try { onRowSave(row); } catch { /* swallow — best effort */ }
        }
      }
      dirtyRowsRef.current.clear();
    };
    // intentionally empty dep array — fires only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opt-in commit auto-save (select/date cells) — see useCommitAutoSave.
  const { onCellCommitted } = useCommitAutoSave({
    enabled: autoSaveCells, columns, dataRef, dirtyRowsRef, onRowSave, clearRowDirty,
  });

  const handleCellChange = useCallback(
    (rowIndex: number, field: string, value: unknown) => {
      markRowDirty(rowIndex);
      onRowChange(rowIndex, field, value);
      onCellCommitted(rowIndex, field, value);
    },
    [onRowChange, markRowDirty, onCellCommitted],
  );

  const handleRowFocus = useCallback(
    (rowIndex: number) => {
      if (activeRowIndex !== null && activeRowIndex !== rowIndex) {
        if (dirtyRowsRef.current.has(activeRowIndex)) {
          const prevRow = dataRef.current[activeRowIndex];
          if (prevRow) onRowSave(prevRow);
          clearRowDirty(activeRowIndex);
        }
      }
      setActiveRowIndex(rowIndex);
    },
    [activeRowIndex, onRowSave, clearRowDirty],
  );

  const handleBlur = useCallback(
    (rowIndex: number, e: React.FocusEvent) => {
      // If focus is moving to a MUI dropdown portal (Select, Menu), don't save yet
      const related = e.relatedTarget as HTMLElement | null;
      if (related) {
        const isPortal = related.closest('[role="listbox"], [role="presentation"], .MuiPopover-root, .MuiMenu-root, .MuiModal-root');
        if (isPortal) return; // Focus went to a dropdown — skip save
      }
      pendingSaveRef.current = rowIndex;
      setTimeout(() => {
        if (pendingSaveRef.current === rowIndex) {
          if (dirtyRowsRef.current.has(rowIndex)) {
            const row = dataRef.current[rowIndex];
            if (row) onRowSave(row);
            clearRowDirty(rowIndex);
          }
          setActiveRowIndex(null);
          pendingSaveRef.current = null;
        }
      }, 200);
    },
    [onRowSave, clearRowDirty],
  );

  const toggleSelection = (key: string) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
    onSelectionChange(Array.from(next));
  };

  // Select-all and the header checkbox reason over VISIBLE rows only, so a
  // filtered view can't bulk-select (and later bulk-delete) hidden rows.
  const visibleKeys = useMemo(
    () => displayRows.map(({ row }) => String(row[keyField])),
    [displayRows, keyField],
  );
  const selectedVisibleCount = useMemo(
    () => visibleKeys.filter((k) => selectedKeys.has(k)).length,
    [visibleKeys, selectedKeys],
  );

  const toggleAll = () => {
    if (visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length) {
      setSelectedKeys(new Set());
      onSelectionChange([]);
    } else {
      setSelectedKeys(new Set(visibleKeys));
      onSelectionChange(visibleKeys);
    }
  };

  const handleFilterChange = useCallback((field: string, next: Set<string> | null) => {
    const nextFilters = { ...columnFilters };
    if (next === null) delete nextFilters[field];
    else nextFilters[field] = next;
    setColumnFilters(nextFilters);
    // Prune the selection to rows still visible — a selected-but-hidden row
    // silently riding into "Remove Selected" would be destructive.
    if (selectedKeys.size > 0) {
      const stillVisible = new Set(
        data
          .filter((row) => rowPassesColumnFilters(row, nextFilters))
          .map((row) => String(row[keyField])),
      );
      const pruned = Array.from(selectedKeys).filter((k) => stillVisible.has(k));
      if (pruned.length !== selectedKeys.size) {
        setSelectedKeys(new Set(pruned));
        onSelectionChange(pruned);
      }
    }
  }, [columnFilters, selectedKeys, data, keyField, onSelectionChange]);

  const clearAllFilters = useCallback(() => setColumnFilters({}), []);

  if (data.length === 0 && !allowAdd) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No samples found</Typography>
      </Box>
    );
  }

  const cellBorder = `1px solid ${alpha(theme.border.color, 0.5)}`;

  const renderCell = (
    row: Record<string, unknown>,
    col: GridColumn,
    rowIndex: number,
  ) => (
    <GridCellRenderer
      row={row}
      col={col}
      rowIndex={rowIndex}
      isActive={activeRowIndex === rowIndex && !readOnly}
      pendingSaveRef={pendingSaveRef}
      onRowChange={readOnly ? () => {} : handleCellChange}
      // onAction is NAVIGATION (open the test workflow), not an edit — keep it
      // for read-only grids so readers can still open tests (view policy).
      onAction={onAction}
      onRowDelete={onRowDelete}
      onRowHistory={onRowHistory}
    />
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: alpha(theme.border.color, 0.6),
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: `0 1px 4px ${alpha('#000', 0.03)}`,
      }}
    >
      {anyFilterActive && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.25,
            borderBottom: cellBorder,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <FilterAltIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Showing {displayRows.length} of {data.length} row{data.length !== 1 ? 's' : ''}
          </Typography>
          <Button size="small" sx={{ py: 0, minWidth: 0 }} onClick={clearAllFilters}>
            Clear filters
          </Button>
        </Box>
      )}
      <TableContainer ref={containerRef} sx={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}>
        {showScrollArrows && (canScrollLeft || canScrollRight) && (
          <GridScrollArrows
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            onScroll={pageBy}
            leftInset={freezeCount > 0 ? frozenPaneWidth : 0}
          />
        )}
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <EditableGridHead
            columns={columns}
            cellBorder={cellBorder}
            sortField={sortField}
            sortDir={sortDir}
            onSortClick={handleSortClick}
            enableColumnFilters={enableColumnFilters}
            filterOptionsByField={filterOptionsByField}
            columnFilters={columnFilters}
            onFilterChange={handleFilterChange}
            selectAllChecked={visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length}
            selectAllIndeterminate={selectedVisibleCount > 0 && selectedVisibleCount < visibleKeys.length}
            onToggleAll={toggleAll}
            freezeCount={freezeCount}
            frozenOffsets={frozenOffsets}
            stickyHeader={stickyHeader}
          />
          <TableBody>
            {displayRows.length === 0 && data.length > 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No rows match the current filters.
                  </Typography>
                  <Button size="small" onClick={clearAllFilters}>Clear filters</Button>
                </TableCell>
              </TableRow>
            )}
            {displayRows.map(({ row, index: idx }, displayIdx) => {
              const rowKey = String(row[keyField]);
              const isSelected = selectedKeys.has(rowKey);
              const isHighlighted = !!(row as any).__highlight;
              const isDirty = dirtyRows.has(idx);
              // Row wash, in precedence order. Dirty = unsaved-changes tint:
              // sits between selected and highlighted on the alpha scale so
              // it's clearly visible but doesn't shout; pairs with the
              // beforeunload guard registered above. Frozen cells re-paint
              // this same tint over an opaque base (layeredBg) so scrolled
              // content can't bleed through them.
              const rowTint = isHighlighted
                ? alpha(theme.palette.warning.light, 0.35)
                : isDirty
                  ? alpha(theme.palette.warning.light, 0.28)
                  : isSelected
                    ? alpha(theme.table.selectedBg, 0.5)
                    : displayIdx % 2 === 1
                      ? alpha(theme.table.stripeBg, 0.4)
                      : undefined;
              const rowHoverTint = isSelected
                ? alpha(theme.table.selectedBg, 0.6)
                : alpha(theme.palette.primary.main, 0.04);
              return (
                <TableRow
                  key={rowKey || idx}
                  sx={{
                    backgroundColor: rowTint,
                    ...(freezeCount > 0 && {
                      '& > td.eg-frozen': layeredBg(theme, rowTint),
                      '&:hover > td.eg-frozen': {
                        backgroundImage: `linear-gradient(${rowHoverTint}, ${rowHoverTint})`,
                      },
                    }),
                    transition: 'background-color 1s ease-out',
                    // Plus a 4px warning-color left edge on the first cell of
                    // dirty rows. Targeting the cell (not the row) avoids the
                    // box-shadow-occluded-by-cells problem that defeated the
                    // first attempt at this indicator.
                    ...(isDirty && {
                      '& > td:first-of-type': {
                        borderLeft: `4px solid ${theme.palette.warning.main}`,
                      },
                    }),
                    ...(isHighlighted && {
                      animation: 'highlightFade 1.5s ease-out forwards',
                      '@keyframes highlightFade': {
                        '0%': { backgroundColor: alpha(theme.palette.warning.light, 0.5) },
                        '60%': { backgroundColor: alpha(theme.palette.warning.light, 0.3) },
                        '100%': { backgroundColor: 'transparent' },
                      },
                    }),
                    '&:hover': {
                      backgroundColor: isSelected
                        ? alpha(theme.table.selectedBg, 0.6)
                        : `${alpha(theme.palette.primary.main, 0.04)} !important`,
                    },
                    height: theme.table.rowHeight + 4,
                    ...(onRowClick && { cursor: 'pointer' }),
                  }}
                  onClick={() => { handleRowFocus(idx); }}
                  onFocusCapture={() => { if (pendingSaveRef.current === idx) pendingSaveRef.current = null; }}
                  onBlur={(e) => handleBlur(idx, e)}
                >
                  <TableCell
                    padding="checkbox"
                    className={freezeCount > 0 ? 'eg-frozen' : undefined}
                    sx={{
                      borderBottom: cellBorder,
                      ...(freezeCount > 0 && { position: 'sticky', left: 0, zIndex: 2 }),
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleSelection(rowKey)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  {columns.map((col, colIdx) => {
                    const isFrozen = colIdx < freezeCount;
                    return (
                      <TableCell
                        key={col.field}
                        className={isFrozen ? 'eg-frozen' : undefined}
                        sx={{
                          borderBottom: cellBorder,
                          py: 0.375,
                          px: 1,
                          ...(isFrozen && {
                            position: 'sticky',
                            left: frozenOffsets[colIdx],
                            zIndex: 2,
                            ...(colIdx === freezeCount - 1 && frozenEdgeSx(theme)),
                          }),
                        }}
                      >
                        {renderCell(row, col, idx)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {allowAdd && (
        <Box sx={{ p: 1 }}>
          <Button size="small" onClick={onAddRow}>
            + Add Row
          </Button>
        </Box>
      )}
    </Paper>
  );
};
